<template>
  <v-container class="keyword-chip-editor-container">
    <v-container class="keyword-chip-editor">
      <v-btn
        v-if="displayRemoveButton"
        class="keyword-remove-button"
        @click="removeKeywordGroup(groupId)"
        text
        size="x-small"
      >
        <v-icon size="18" class="pa-0 ma-0">fa-times</v-icon>
      </v-btn>
      <v-row row>
        <v-col class="col-12">
          <KeywordChipDisplay
            v-model="value"
            :label="label"
            @keyword-removed="keywordRemoveAction"
            :chip="chip"
            :chipSuggestion="chipSuggestion"
          ></KeywordChipDisplay>
        </v-col>
      </v-row>
      <v-row row>
        <v-col class="col-9">
          <v-text-field
            ref="newKeyword"
            class="keyword-entry"
            @keyup.enter="addKeyword()"
            @keyup.188="addKeyword()"
            :loading="isLoading"
            :disabled="isDisabled"
            :error="hasError"
            :error-messages="errorMessage"
            label="Enter Keyword"
            text
          ></v-text-field>
        </v-col>
        <v-col class="col-3">
          <v-checkbox
            v-model="useSemantic"
            label="Related Keywords"
            solo
            :disabled="isDisabled"
            class="ml-2"
          ></v-checkbox>
        </v-col>
      </v-row>
    </v-container>
  </v-container>
</template>

<script>
import lodashFind from "lodash/find";
import { getSemanticWords } from "@/services/common/semantic.service.js";
import { translate } from "@/services/common/translator.service";

const EMITTED_ACTIONS = {
  KEYWORD_GROUP_REMOVED: "keyword-group-removed",
};

export default {
  name: "KeywordChipEditor",
  components: {
    KeywordChipDisplay: () =>
      import("@/components/alerts/wizard/keywords/KeywordChipDisplay"),
  },
  props: {
    value: {
      type: Array,
      required: true,
      default() {
        return [];
      },
    },
    label: {
      type: String,
    },
    displayRemoveButton: {
      type: Boolean,
      default: true,
    },
    groupId: {
      type: Number,
    },
    disableInput: {
      type: Boolean,
      default: false,
    },
    chip: {
      type: Object,
      default() {
        return {
          font: "black",
          color: "",
        };
      },
    },
    chipSuggestion: {
      type: Object,
      default() {
        return {
          font: "#fff",
          color: "#4B49FF",
        };
      },
    },
  },
  data() {
    return {
      useSemantic: false,
      isLoading: false,
      errorMessage: "",
      symbolLanguages: [{ name: "Chinese", code: "cn" }],
    };
  },
  computed: {
    hasError() {
      return this.errorMessage.length > 0;
    },
    isDisabled() {
      return this.disableInput || this.isLoading;
    },
  },
  methods: {
    keywordRemoveAction() {
      const text = this.$refs.newKeyword.internalValue;
    },
    isValidText(text, showMessage) {
      if (!text || text.length < 3) {
        this.errorMessage = showMessage ? "Must be 3 characters or more" : "";
        return false;
      }
      this.errorMessage = "";
      return true;
    },
    async addKeyword() {
      this.isLoading = true;
      const keywordVal = {
        text: this.$refs.newKeyword.internalValue.trim(),
        isSuggestion: false,
      };

      const clearText = await this.appendKeywordToList(keywordVal);
      if (this.useSemantic) {
        this.addSemantic(keywordVal.text);
      }

      if (clearText) {
        this.$refs.newKeyword.internalValue = "";
      }
      this.isLoading = false;
      this.focusInputBox();
    },
    async addSemantic(text) {
      this.isLoading = true;
      try {
        const results = await getSemanticWords({
          word: encodeURI(text),
        });
        results.synonyms.map(async (synonym) => {
          const textVal = Object.keys(synonym)[0];
          if (!this.isValidText(textVal, false)) {
            return;
          }
          await this.appendKeywordToList({
            text: textVal,
            isSuggestion: true,
          });
        });
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
        this.focusInputBox();
      }
    },
    async appendKeywordToList(keyword) {
      let present = false;
      for (let i = 0; i < this.value.length; i++) {
        if (this.value[i].text === keyword.text) {
          present = true;
        }
      }
      if (!present) {
        let cont = true;
        if (keyword.text.length < 3) {
          if (this.featureUnlocked("alerts-keyword-symbol-langs")) {
            let isSymbolLang = false;
            const language = await this.getChipTextLanguage(keyword);
            if (language) {
              this.symbolLanguages.forEach((lang) => {
                if (lang.code.toLowerCase() === language.toLowerCase()) {
                  isSymbolLang = true;
                }
              });
            }
            if (isSymbolLang) {
              if (keyword.text.length < 2) {
                let l = lodashFind(this.symbolLanguages, ["code", language]);
                if (l) {
                  this.errorMessage = `Must be 2 characters or more for ${l.name}`;
                }
                cont = false;
              }
            } else {
              this.errorMessage = `Must be 3 characters or more`;
              cont = false;
            }
          } else {
            this.errorMessage = `Must be 3 characters or more`;
            cont = false;
          }
        }
        if (cont) {
          this.value.push(keyword);
          this.errorMessage = "";
          return true;
        }
        return false;
      }
    },
    removeKeywordGroup(groupId) {
      this.$emit(EMITTED_ACTIONS.KEYWORD_GROUP_REMOVED, { groupId });
    },
    focusInputBox() {
      this.$nextTick(() => this.$refs.newKeyword.focus());
    },
    async getChipTextLanguage(keyword) {
      let params = {
        text: keyword.text,
        dest: "en",
      };
      const RESULT = await translate(params);
      if (RESULT && Array.isArray(RESULT) && RESULT.length) {
        if (RESULT[0].detectedSourceLanguage) {
          const lang = RESULT[0].detectedSourceLanguage;
          if (lang) {
            const langParts = lang.split("-");
            if (langParts && Array.isArray(langParts) && langParts.length > 1) {
              return langParts[1].toLowerCase();
            } else {
              return langParts[0].toLowerCase();
            }
          }
        }
      }
      return null;
    },
  },
};
</script>
<style scoped>
.keyword-remove-button {
  position: absolute;
  right: 20px;
  top: 20px;
  padding: 0 !important;
  margin: 0;
  height: 16px !important;
  min-width: 16px !important;
}
.keyword-chip-editor-container {
  position: relative;
  padding: 10px;
}
.keyword-chip-editor {
  background-color: white;
  padding: 5px 5px 6px 5px;
  border-radius: 10px;
  box-shadow: 0 3px 1px -2px rgba(0, 0, 0, 0.2), 0 2px 2px 0 rgba(0, 0, 0, 0.14),
    0 1px 5px 0 rgba(0, 0, 0, 0.12);
}
.keyword-chip-editor >>> .v-input__slot {
  padding-left: 0 !important;
  padding-right: 0 !important;
  margin: 0 !important;
}
</style>
